import { createContext, useEffect, useState } from 'react'
import { useIdleTimer } from 'react-idle-timer'
import Actions from '../../actions'
import { Route } from 'react-router-dom'
import { Redirect, Switch, useHistory, useLocation } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import { APP_VERSION, BNPL_Feature_flag, routes } from '../../consts'
import config from '../../config.json'
import MainLayout from '../MainLayout'
import GetStarted from '../pages/GetStarted'
import VideoCall from '../pages/VideoCall'
import Home from '../pages/Home'
import InvalidIp from '../pages/InvalidIp'
import PaymentPlan from '../pages/PaymentPlan'
import PaymentPage from '../pages/GetStarted/PaymentPage'
import WalnutPage from '../pages/GetStarted/WalnutPage'
import PaymentSuccess from '../pages/ExternalPaymentSuccessPage'
import PaymentCancel from '../pages/PaymentCancel'
import ThankYou from '../pages/ThankYou'
import AskNicely from '../pages/AskNicely'
import MobileWebUserBlocker from '../pages/GetStarted/MobileWebUserBlocker'
import { AppRoutes } from '../MainLayout/Routes'
import ExternalPaymentSuccessPage from '../pages/ExternalPaymentSuccessPage/ExternalPaymentSuccessPage'
import { event, eventTypes } from '../../consts/eventConsts'
import ZouraBillingForm from '../pages/ZouraBillingForm/ZouraBillingForm'
import { getAge } from '../../utils/familyUtils'
import { getLocale, setLocale } from '../../i18n'
import { Spin } from 'antd'

import './App.css'
import {
  checkIfNeedToRedirectToCommunicationPerferences,
  checkIfNeedToRedirectToSecretQuestions
} from '../../utils/onBoardingUtils'
import { getIsHmoMember } from '../../utils/hmoUtils'

export const LanguageContext = createContext(null)

const App = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const currentLocation = useLocation()
  const { isLoggedIn, pathnameToNavigateAfterLoggedIn } = useSelector(
    state => state.auth
  )
  const locale = useSelector(locale => locale?.locale)
  const selectedService = useSelector(state => state.getCare.selectedService)
  const credits = useSelector(state => state.payment.credits)
  const subscriptions = useSelector(state => state.payment.my_subscriptions)
  const roomName = useSelector(state => state.videoCall.roomName)
  const isCallActive = useSelector(state => state.videoCall.isCallActive)
  const { autoSignoutTimeoutInSeconds } = config
  const userDetails = useSelector(state => state.profile?.userDetails)
  const isUserDetailsLoading = useSelector(
    state => state.profile?.isUserDetailsLoading
  )
  const isHmoMember = getIsHmoMember(userDetails)
  const isProvidersPage = currentLocation.pathname.includes('providers')

  const [isDoneLoading, setIsDoneLoading] = useState(false)
  const [language, changeLanguage] = useState('en')

  const onIdle = () => {
    if (!isCallActive && isLoggedIn) {
      dispatch(Actions.createAction(Actions.SIGNOUT_REQUESTED))
    }
  }
  const hasCredits = () => {
    if (
      (subscriptions && subscriptions?.length <= 0) ||
      (credits !== null && credits > 0)
    ) {
      return true
    } else {
      return false
    }
  }

  useEffect(() => {
    // switching theme to hmo
    if (!isHmoMember && !isProvidersPage && isLoggedIn) {
      document.documentElement.setAttribute('theme', 'telehealth')
    } else {
      document.documentElement.removeAttribute('theme', 'telehealth')
    }
  }, [isHmoMember, isProvidersPage, isLoggedIn])

  // reload app when locale change (updating the context)
  useEffect(() => {
    changeLanguage(locale)
  }, [locale])

  useEffect(() => {
    let selService = selectedService
    let has_credit = hasCredits()
    let first_name = userDetails?.firstName
    let last_name = userDetails?.lastName
    let email = userDetails?.email
    let phone = userDetails?.phoneNumber
    let user_id = userDetails?.id
    let gender = userDetails?.sex
    let age = getAge(userDetails?.dateOfBirth)
    let is_hmo = getIsHmoMember(userDetails)
    return history.listen(location => {
      if (location.pathname !== currentLocation.pathname) {
        let data = {
          url: `${window.location.protocol}//${window.location.host}${window.location.pathname}`,
          template: Object.keys(routes).find(
            key => routes[key] === location.pathname
          ),
          title: window.document?.title,
          website_version: APP_VERSION,
          // title: 'Antidoe'
          has_credit,
          first_name,
          last_name,
          email,
          phone,
          user_id,
          gender,
          age,
          is_hmo
        }
        if (selService) {
          let user_scope = {
            clinic: selService,
            has_credit
          }
          data = { ...data, ...user_scope }
        }
        dispatch(
          Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
            type: eventTypes.pageViewEvent,
            eventName: event.pageViewEvent.page_view,
            data: data
          })
        )
      }
    })
  }, [history, selectedService, credits, subscriptions, userDetails])

  const { start, reset } = useIdleTimer({
    timeout: autoSignoutTimeoutInSeconds * 1000,
    onIdle,
    startOnMount: false,
    events: [
      'mousemove',
      'keydown',
      'wheel',
      'DOMMouseScroll',
      'mousewheel',
      'mousedown',
      'touchstart',
      'touchmove',
      'MSPointerDown',
      'MSPointerMove'
    ]
  })
  const location = useLocation()

  useEffect(() => {
    // get local from storage (if there is one) and set it
    setLocale(getLocale() || 'en').then(() => setIsDoneLoading(true))

    if (!isLoggedIn) {
      dispatch(
        Actions.createAction(
          Actions.SAVE_LOCATION_TO_NAVIGATE_AFTER_LOGGED_IN,
          {
            pathname: location.pathname
          }
        )
      )
    }
  }, [])

  useEffect(() => {
    if (isLoggedIn) {
      start()
      dispatch(
        Actions.createAction(Actions.IS_SIGNED_IN_RECEIVED, {
          isSignedIn: true
        })
      )
      dispatch(
        Actions.createAction(Actions.STORE_RESET_HANDLER, {
          reset
        })
      )

      dispatch(Actions.createAction(Actions.NOTIFICATIONS_REQUESTED))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn])

  if (!isDoneLoading) {
    return <Spin size='large' className='center-spinner' />
  }

  // in Case we should present the secret questions page (in the user is hmo and didn't have secret questions yet),
  // navigate to secret questions without showing the home page
  // in case we should present the communication and preference (from the onboarding flow, not the configure flow from profile),
  // navigate to the communication and preference page without showing the home page
  // else show the home page
  const getRedirectOnRoot = () => {
    // Don't make redirect decisions while profile is loading
    if (isUserDetailsLoading) {
      return <Spin size='large' className='center-spinner' />
    }

    if (userDetails?.status === 'telehealth_to_hmo_enrolled')
      dispatch(
        Actions.createAction(Actions.TH_TO_HMO_ENROLLED_SIGNUP_REQUESTED)
      )

    if (checkIfNeedToRedirectToSecretQuestions(userDetails))
      return <Redirect to={routes.SECRET_QUESTIONS} />

    if (checkIfNeedToRedirectToCommunicationPerferences(userDetails))
      return <Redirect to={routes.PROFILE_ROUTE.COMMUNICATION_PREFERENCES} />

    return <Home />
  }

  return (
    <LanguageContext.Provider value={{ language, changeLanguage }}>
      <MainLayout>
        <Switch>
          <Route path={routes.ROOT} exact>
            {isLoggedIn ? (
              getRedirectOnRoot()
            ) : (
              <Redirect to={routes.SIGN_IN} />
            )}
          </Route>
          <Route path={routes.VIDEO_CALL} exact>
            {!!roomName ? <VideoCall /> : <Redirect to={routes.ROOT} />}
          </Route>
          <Route path={routes.SIGN_IN}>
            {isLoggedIn && !!userDetails ? (
              <Redirect
                to={
                  pathnameToNavigateAfterLoggedIn
                    ? pathnameToNavigateAfterLoggedIn
                    : routes.ROOT
                }
              />
            ) : (
              <GetStarted />
            )}
          </Route>
          <Route
            path={routes.FIND_A_PROVIDER}
            exact
            render={renderObject => {
              const isChangePcp =
                renderObject?.location?.search?.includes('changePcp=true')
              const state = renderObject?.location?.search
                .split('&')
                .find(param => param.includes('state='))
              return isChangePcp ? (
                <Redirect
                  to={`${
                    routes.SEARCH_PROVIDERS_RESULTS
                  }/?query=PCP&changePcp=true${state ? `&state=${state}` : ''}`}
                />
              ) : (
                <Redirect to={routes.SEARCH_PROVIDERS} />
              )
            }}
          />
          <Route path={routes.PROVIDER_SEARCH_RESULTS} exact>
            <Redirect to={routes.SEARCH_PROVIDERS_RESULTS} />
          </Route>
          <Route
            path={routes.PROVIDER_PAGE}
            exact
            render={({ match }) => (
              <Redirect
                to={`${routes.SEARCH_PROVIDERS_RESULTS}/${match.params.id}`}
              />
            )}
          />
          {AppRoutes?.map((route, index) => {
            return !route.children ? (
              <route.route
                key={index}
                path={route?.path}
                exact={route?.exact}
                component={route?.component}
                componentFunc={route?.componentFunc}
                permissions={route?.permissions}
                disabled={route?.disabled}
                filter={route?.filter}
              ></route.route>
            ) : (
              route.children
                .filter(route => !!route?.path)
                ?.map((childRoute, i) => (
                  <childRoute.route
                    key={i}
                    path={childRoute?.path}
                    exact={childRoute?.exact}
                    component={childRoute?.component}
                    permissions={childRoute?.permissions}
                    disabled={route?.disabled}
                    filter={childRoute?.filter}
                  ></childRoute.route>
                ))
            )
          })}
          <Route path={routes.SIGN_UP}>
            {isLoggedIn ? <Redirect to={routes.ROOT} /> : <GetStarted />}
          </Route>
          <Route path={routes.CHOOSE_PASSWORD}>
            <Redirect to={routes.ROOT} />
          </Route>
          <Route path={routes.ONE_TIME_LOGIN}>
            {isLoggedIn ? <Redirect to={routes.ROOT} /> : <GetStarted />}
          </Route>

          <Route path={routes.RESET_AND_CHOOSE_NEW_PASSWORD}>
            {isLoggedIn ? <Redirect to={routes.ROOT} /> : <GetStarted />}
          </Route>

          <Route path={routes.INVALID_IP} component={InvalidIp} />
          {BNPL_Feature_flag && (
            <Route path={routes.PAYMENT_PLAN} component={PaymentPlan} />
          )}
          <Route path={routes.PAYMENT_PLAN_FORM}>
            <PaymentPlan isFormOnly={true} />
          </Route>
          <Route path={routes.ZUORA_PAYMENT_PAGE}>
            <ZouraBillingForm />
          </Route>
          <Route path={routes.PAYMENT_PAGE} component={PaymentPage} />

          <Route path={routes.WALNUT_PAGE} component={WalnutPage} />
          <Route path={routes.PAYMENT_SUCCESS} component={PaymentSuccess} />
          <Route
            path={routes.EXTERNAL_PAYMENT_SUCCESS_PAGE}
            component={ExternalPaymentSuccessPage}
          />
          <Route path={routes.PAYMENT_CANCEL} component={PaymentCancel} />

          <Route path={routes.THANK_YOU_PAGE} exact component={ThankYou} />
          <Route path={routes.SURVEY_PAGE} exact component={AskNicely} />
          <Route
            Path={routes.BLOCK_MOBILE_WEB}
            component={MobileWebUserBlocker}
          />
        </Switch>
      </MainLayout>
    </LanguageContext.Provider>
  )
}

export default App
